import Axios from '../services/Axios'
import type {
  CreateLinkedAccountPayload,
  LinkedAccountItem,
  UpdateLinkedAccountPayload,
} from '../types/linkedAccount'

/**
 * Sáu endpoint của SP-005 — chỗ DUY NHẤT chạm backend của luồng tài khoản liên kết.
 * Hợp đồng: `docs/sp-005/BE-api-contract.md` §4.
 *
 * Bóc hai lớp vỏ: axios bọc `response.data`, erp-gateway bọc `{ statusCode, data }` ⇒ dữ liệu
 * thật ở `response.data.data`. `POST` trả HTTP 201 nhưng `body.statusCode` vẫn là 200 — đừng
 * bao giờ so `statusCode === 201` (cạm bẫy #1 của BE).
 */

/** `src/services/Axios.js` là JS thuần — khai interface hẹp đúng phần đang dùng, không `any`. */
interface HttpClient {
  get<T>(url: string): Promise<{ data: T }>
  post<T>(url: string, body?: unknown): Promise<{ data: T }>
  put<T>(url: string, body?: unknown): Promise<{ data: T }>
  delete<T>(url: string): Promise<{ data: T }>
}

interface GatewayEnvelope<T> {
  statusCode: number
  data: T
}

const http = (): HttpClient => (
  Axios as unknown as { getInstance: () => HttpClient }
).getInstance()

/** #1 — danh sách của MỘT tài khoản. Đối tác: truyền `representative_id`, không phải id bản ghi đối tác. */
export async function listLinkedAccounts(userId: string): Promise<LinkedAccountItem[]> {
  const response = await http().get<GatewayEnvelope<LinkedAccountItem[]>>(
    `/api/v1/accounts/${userId}/linked-accounts`,
  )
  return response.data.data
}

/** #2 — danh sách của chính người đang đăng nhập (màn "Tài khoản của tôi"). */
export async function listMyLinkedAccounts(): Promise<LinkedAccountItem[]> {
  const response = await http().get<GatewayEnvelope<LinkedAccountItem[]>>(
    '/api/v1/my/linked-accounts',
  )
  return response.data.data
}

/** #3 — thêm. */
export async function createLinkedAccount(
  userId: string,
  payload: CreateLinkedAccountPayload,
): Promise<LinkedAccountItem> {
  const response = await http().post<GatewayEnvelope<LinkedAccountItem>>(
    `/api/v1/accounts/${userId}/linked-accounts`,
    payload,
  )
  return response.data.data
}

/** #4 — sửa. Một request vừa xác thực vừa ghi (BRD `BR-18`), không tách hai bước. */
export async function updateLinkedAccount(
  id: string,
  payload: UpdateLinkedAccountPayload,
): Promise<LinkedAccountItem> {
  const response = await http().put<GatewayEnvelope<LinkedAccountItem>>(
    `/api/v1/linked-accounts/${id}`,
    payload,
  )
  return response.data.data
}

/** #5 — xoá mềm. */
export async function deleteLinkedAccount(id: string): Promise<void> {
  await http().delete<GatewayEnvelope<{ id: string }>>(`/api/v1/linked-accounts/${id}`)
}

/**
 * #6 — endpoint DUY NHẤT trả mật khẩu tài khoản liên kết.
 * Giá trị trả về chỉ được giữ trong state của hộp thoại đang mở (BE §6.1).
 */
export async function revealLinkedAccountPassword(
  id: string,
  currentPassword: string,
): Promise<string> {
  const response = await http().post<GatewayEnvelope<{ password: string }>>(
    `/api/v1/linked-accounts/${id}/reveal-password`,
    { current_password: currentPassword },
  )
  return response.data.data.password
}
